'use client';
import React from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import type { GI } from './GIContent';

export default function GIDetailPanel({ gi, onClose }: { gi: GI; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-primary/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl bg-card h-full overflow-y-auto scrollbar-thin shadow-modal flex flex-col fade-in">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border bg-card sticky top-0 z-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono-data text-base font-700 text-foreground">{gi.giNumber}</span>
              <StatusBadge status={gi.status as Parameters<typeof StatusBadge>[0]['status']} />
              {gi.signatureCaptured && (
                <span className="flex items-center gap-1 text-xs text-teal">
                  <Icon name="PencilSquareIcon" size={12} />
                  Signed
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{gi.requestedBy} · {gi.site}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        <div className="flex-1 px-6 py-5 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'Requested By', value: gi.requestedBy },
              { label: 'Site', value: gi.site },
              { label: 'Created Date', value: gi.createdDate },
              { label: 'BOM / Project', value: gi.bomCode ? `${gi.bomCode} — ${gi.bomProject}` : 'Not linked' },
              { label: 'Total Value', value: `€ ${gi.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`, bold: true },
              { label: 'Line Items', value: `${gi.lineItems.length} items` },
            ].map((m) => (
              <div key={`gi-meta-${m.label}`}>
                <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground">{m.label}</p>
                <p className={`text-sm mt-0.5 ${m.bold ? 'font-700 text-foreground' : 'text-foreground'}`}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Purpose */}
          <div className="bg-muted/40 rounded-lg px-4 py-3">
            <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground mb-1">Purpose / Justification</p>
            <p className="text-sm text-foreground leading-relaxed">{gi.purpose}</p>
          </div>

          {/* Line items */}
          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Line Items</h4>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {['Item', 'Requested', 'Issued', 'Unit Cost', 'Total', 'Availability'].map((h) => (
                      <th key={`gi-dp-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {gi.lineItems.map((li) => {
                    const canFulfill = li.availableQty >= li.requestedQty;
                    return (
                      <tr key={li.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-3 py-2.5">
                          <p className="text-xs font-600 text-foreground">{li.itemName}</p>
                          <p className="text-[11px] font-mono-data text-muted-foreground">{li.itemCode}</p>
                        </td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-foreground">{li.requestedQty} {li.uom}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-teal font-500">
                          {li.issuedQty > 0 ? `${li.issuedQty} ${li.uom}` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-foreground">€ {li.unitCost.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums font-600 text-foreground">€ {li.totalCost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2.5">
                          {canFulfill ? (
                            <span className="text-[10px] bg-teal/10 text-teal px-2 py-0.5 rounded-full font-500 border border-teal/20">
                              {li.availableQty} avail.
                            </span>
                          ) : (
                            <span className="text-[10px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-500 border border-red-200">
                              Short {li.requestedQty - li.availableQty} {li.uom}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/30">
                    <td colSpan={4} className="px-3 py-2.5 text-xs font-600 text-right text-muted-foreground">Total Value</td>
                    <td colSpan={2} className="px-3 py-2.5 text-sm font-700 text-foreground tabular-nums">
                      € {gi.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* BOM impact */}
          {gi.bomCode && (
            <div className="bg-secondary/5 border border-secondary/20 rounded-lg px-4 py-3">
              <p className="text-xs font-600 text-secondary mb-2 flex items-center gap-1.5">
                <Icon name="ClipboardDocumentListIcon" size={13} />
                BOM Consumption Tracking
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-500 text-foreground">{gi.bomCode} — {gi.bomProject}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {gi.status === 'ISSUED' ? 'BOM consumption updated on issuance.' : 'Consumption will update on physical issuance.'}
                  </p>
                </div>
                <button
                  onClick={() => toast.info(`Opening BOM ${gi.bomCode}…`)}
                  className="text-xs text-secondary hover:underline flex items-center gap-1"
                >
                  View BOM
                  <Icon name="ArrowTopRightOnSquareIcon" size={11} />
                </button>
              </div>
            </div>
          )}

          {/* Approval info */}
          {gi.approvedBy && (
            <div className="bg-teal/5 border border-teal/20 rounded-lg px-4 py-3 flex items-center gap-3">
              <Icon name="CheckCircleIcon" size={18} className="text-teal shrink-0" />
              <div>
                <p className="text-xs font-600 text-teal">Approved by {gi.approvedBy}</p>
                <p className="text-xs text-muted-foreground">{gi.approvedDate}</p>
              </div>
            </div>
          )}

          {/* Issuance info */}
          {gi.issuedDate && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <p className="text-xs font-600 text-blue-700 mb-2 flex items-center gap-1.5">
                <Icon name="TruckIcon" size={13} />
                Physical Issuance Record
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Issued Date</p>
                  <p className="text-sm font-500 text-foreground">{gi.issuedDate}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Recipient</p>
                  <p className="text-sm font-500 text-foreground">{gi.recipient}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Signature</p>
                  <p className="text-sm font-500 text-foreground flex items-center gap-1.5">
                    {gi.signatureCaptured ? (
                      <>
                        <Icon name="CheckCircleIcon" size={13} className="text-teal" />
                        Captured
                      </>
                    ) : 'Not captured'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Gate Pass</p>
                  <p className="text-sm font-500 text-foreground flex items-center gap-1.5">
                    {gi.gatePasPrinted ? (
                      <>
                        <Icon name="PrinterIcon" size={13} className="text-secondary" />
                        Printed
                      </>
                    ) : 'Not printed'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center gap-3">
          {gi.status === 'ISSUED' && (
            <>
              <button
                onClick={() => toast.info(`Generating gate pass PDF for ${gi.giNumber}…`)}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg text-sm font-500 hover:bg-secondary/90 transition-all active:scale-95"
              >
                <Icon name="PrinterIcon" size={15} />
                Reprint Gate Pass
              </button>
              <button
                onClick={() => toast.success(`GI report downloaded for ${gi.giNumber}.`)}
                className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors"
              >
                <Icon name="ArrowDownTrayIcon" size={15} />
                Download PDF
              </button>
            </>
          )}
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors ml-auto">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}